/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

// Multi-select picker styled to match the rest of the HeroSemantic dropdowns:
// a trigger button that displays selected items as removable chips, plus a
// popover with a checkbox list. Inspired by HeroSelectField in htm-admin-hero
// but built with plain HTML + state so it works under the project's existing
// SSR/jsdom shim without pulling in react-aria-components specifics.

const normalizeValueToArray = value => {
  if (value === undefined || value === null || value === '') {return [];}
  if (Array.isArray(value)) {return value;}
  if (value instanceof Set) {return Array.from(value);}
  return [value];
};

const HeroSelectField = ({
  ariaLabel,
  className,
  disabled,
  id,
  multiple,
  name,
  onChange,
  options,
  placeholder,
  value,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const selected = useMemo(() => normalizeValueToArray(value).map(String), [value]);

  useEffect(() => {
    if (!open) {return undefined;}
    const handlePointerDown = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [open]);

  const isSelected = optionValue => selected.includes(String(optionValue));

  const toggleOption = optionValue => {
    if (!multiple) {
      onChange(optionValue);
      setOpen(false);
      return;
    }
    const next = isSelected(optionValue) ?
      selected.filter(v => v !== String(optionValue))
      : [...selected, String(optionValue)];
    // Preserve the original value shape (numbers stay numbers) by mapping back
    // through the option list.
    const nextValues = next.map(strVal => {
      const opt = options.find(o => String(o.value) === strVal);
      return opt ? opt.value : strVal;
    });
    onChange(nextValues);
  };

  const removeOption = (event, optionValue) => {
    event.stopPropagation();
    if (!multiple) {
      onChange('');
      return;
    }
    const nextValues = selected
      .filter(v => v !== String(optionValue))
      .map(strVal => {
        const opt = options.find(o => String(o.value) === strVal);
        return opt ? opt.value : strVal;
      });
    onChange(nextValues);
  };

  const selectedOptions = options.filter(opt => isSelected(opt.value));

  return (
    <div ref={containerRef} className={`relative w-full ${className || ''}`}>
      <button
        type="button"
        id={id}
        name={name}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen(prev => !prev)}
        className={[
          'flex w-full min-h-10 items-center justify-between gap-2 rounded-lg border bg-[var(--owner-surface)]',
          'px-3 py-1.5 text-sm leading-5 text-[var(--owner-text)] shadow-sm outline-none transition-colors',
          'border-[var(--owner-border-strong)] hover:border-[var(--tenant-primary)]',
          'focus-visible:border-[var(--tenant-primary)] focus-visible:ring-2 focus-visible:ring-[var(--owner-focus-ring)]',
          disabled && 'cursor-not-allowed opacity-60',
        ].filter(Boolean).join(' ')}
      >
        <div className="flex flex-1 flex-wrap items-center gap-1.5 text-left">
          {selectedOptions.length === 0 && (
            <span className="text-[var(--owner-text-muted)]">{placeholder}</span>
          )}
          {selectedOptions.map(opt => (
            <span
              key={String(opt.value)}
              className="inline-flex items-center gap-1 rounded-full bg-[var(--owner-accent-subtle)] px-2.5 py-0.5 text-xs font-semibold text-[var(--owner-accent-readable)]"
            >
              {opt.text || (typeof opt.label === 'string' ? opt.label : null)}
              {!disabled && (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Remove ${opt.label || opt.text}`}
                  onClick={e => removeOption(e, opt.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      removeOption(e, opt.value);
                    }
                  }}
                  className="inline-flex items-center justify-center rounded-full hover:bg-black/10"
                >
                  <X size={12} />
                </span>
              )}
            </span>
          ))}
        </div>
        <ChevronDown size={16} aria-hidden className="shrink-0 text-[var(--owner-text-muted)]" />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-multiselectable={multiple || undefined}
          className={[
            'absolute z-30 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-[var(--owner-border-strong)]',
            'bg-[var(--owner-surface)] py-1 shadow-lg outline-none',
          ].join(' ')}
        >
          {options.map(opt => {
            const checked = isSelected(opt.value);
            const isRichLabel = opt.label && typeof opt.label === 'object';
            const badgeColor = isRichLabel ? (opt.label.color === 'green' ? 'success' : 'danger') : null;
            return (
              <li
                key={String(opt.key ?? opt.value)}
                role="option"
                aria-selected={checked}
                aria-disabled={opt.disabled || undefined}
                onClick={() => { if (!opt.disabled) { toggleOption(opt.value); } }}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (!opt.disabled) { toggleOption(opt.value); }
                  }
                }}
                tabIndex={opt.disabled ? -1 : 0}
                className={[
                  'flex items-center gap-2 px-3 py-2 text-sm leading-5',
                  'text-[var(--owner-text)]',
                  opt.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-[var(--owner-accent-subtle)]',
                  checked && !opt.disabled && 'bg-[var(--owner-accent-subtle)] font-semibold',
                ].filter(Boolean).join(' ')}
              >
                {multiple && (
                  <input
                    type="checkbox"
                    readOnly
                    checked={checked}
                    tabIndex={-1}
                    aria-hidden="true"
                    className="pointer-events-none h-4 w-4 accent-[var(--tenant-primary)]"
                  />
                )}
                <span className="flex-1 flex items-center gap-2">
                  {opt.text || (typeof opt.label === 'string' ? opt.label : null)}
                  {isRichLabel && (
                    <span
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                      style={{
                        background: `color-mix(in srgb, var(--owner-${badgeColor}) 14%, transparent)`,
                        color: `var(--owner-${badgeColor})`,
                      }}
                    >
                      {opt.label.detail.content}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

HeroSelectField.propTypes = {
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  multiple: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        color: PropTypes.string,
        detail: PropTypes.shape({ content: PropTypes.string }),
      }),
    ]),
    text: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  })).isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    PropTypes.number,
    PropTypes.string,
  ]),
};

export default HeroSelectField;
